/**
 * PriceLab API Client
 * Replaces IndexedDB with PostgreSQL backend
 */

// API response types (snake_case from backend)
// Full typed interfaces will be added when database.ts wrapper is eliminated
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiRecord = Record<string, any>;

// API base URL - configured via environment variable
// In production, set REACT_APP_API_URL in .env or build process
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Generic fetch wrapper (expects { success: true, data: ... } format)
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  // Get SSO token from localStorage
  const token = localStorage.getItem('sso_access_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add Authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    headers,
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'API request failed');
  }

  return data.data;
}


// ============================================
// PRODUCTS
// ============================================

export const apiGetAllProducts = () =>
  apiRequest<ApiRecord[]>('/products?limit=20000'); // Request all products (no pagination)

export const apiGetProduct = (id: string) =>
  apiRequest<ApiRecord>(`/products/${id}`);

export const apiSaveProduct = (product: ApiRecord) =>
  apiRequest<ApiRecord>('/products', {
    method: 'POST',
    body: JSON.stringify(product),
  });

export const apiUpdateProduct = (product: ApiRecord & { id: string }) =>
  apiRequest<ApiRecord>(`/products/${product.id}`, {
    method: 'PUT',
    body: JSON.stringify(product),
  });

export const apiDeleteProduct = (id: string) =>
  apiRequest<void>(`/products/${id}`, {
    method: 'DELETE',
  });

export const apiBulkSaveProducts = (products: ApiRecord[]) =>
  apiRequest<{ added: number; updated: number }>('/products/bulk', {
    method: 'POST',
    body: JSON.stringify({ products }),
  });

export const apiGetProductStats = () =>
  apiRequest<{ total_products: string; categories_count: string; last_updated: string | null }>('/products/stats/summary');

export const apiGetProductCategories = () =>
  apiRequest<string[]>('/products/categories');

// ============================================
// SKU MAPPINGS
// ============================================

export const apiGetSKUMappingsByProduct = (productId: string) =>
  apiRequest<ApiRecord[]>(`/sku-mappings/product/${productId}`);

export const apiGetSKUMappingsByPlatform = (platform: string, marketplaceCode?: string) => {
  const params = new URLSearchParams({ platform });
  if (marketplaceCode) params.append('marketplaceCode', marketplaceCode);
  return apiRequest<ApiRecord[]>(`/sku-mappings?${params}`);
};

export const apiFindSKUMapping = (platform: string, sku: string) =>
  apiRequest<ApiRecord | null>(`/sku-mappings/find?platform=${platform}&sku=${sku}`);

export const apiSaveSKUMapping = (mapping: ApiRecord) =>
  apiRequest<ApiRecord>('/sku-mappings', {
    method: 'POST',
    body: JSON.stringify(mapping),
  });

export const apiUpdateSKUMapping = (mapping: ApiRecord & { id: string }) =>
  apiRequest<ApiRecord>(`/sku-mappings/${mapping.id}`, {
    method: 'PUT',
    body: JSON.stringify(mapping),
  });

export const apiDeleteSKUMapping = (id: string) =>
  apiRequest<void>(`/sku-mappings/${id}`, {
    method: 'DELETE',
  });

// ============================================
// CATEGORY EXPENSES
// ============================================

export const apiGetAllCategoryExpenses = () =>
  apiRequest<ApiRecord[]>('/products/category-expenses');

export const apiGetCategoryExpense = (category: string, platform: string, marketplaceCode: string) => {
  const params = new URLSearchParams({ category, platform, marketplaceCode });
  return apiRequest<ApiRecord | null>(`/products/category-expenses/find?${params}`);
};

export const apiGetCategoryExpensesByPlatform = (platform: string, marketplaceCode?: string) => {
  const params = new URLSearchParams({ platform });
  if (marketplaceCode) params.append('marketplaceCode', marketplaceCode);
  return apiRequest<ApiRecord[]>(`/products/category-expenses?${params}`);
};

export const apiUpsertCategoryExpense = (expense: ApiRecord) =>
  apiRequest<ApiRecord>('/products/category-expenses', {
    method: 'POST',
    body: JSON.stringify(expense),
  });

// ============================================
// MARKETPLACES
// ============================================

export const apiGetAllMarketplaces = () =>
  apiRequest<ApiRecord[]>('/marketplaces');

export const apiGetMarketplace = (id: string) =>
  apiRequest<ApiRecord>(`/marketplaces/${id}`);

export const apiSaveMarketplace = (marketplace: ApiRecord) =>
  apiRequest<ApiRecord>('/marketplaces', {
    method: 'POST',
    body: JSON.stringify(marketplace),
  });

export const apiUpdateMarketplace = (marketplace: ApiRecord & { id: string }) =>
  apiRequest<ApiRecord>(`/marketplaces/${marketplace.id}`, {
    method: 'PUT',
    body: JSON.stringify(marketplace),
  });

export const apiDeleteMarketplace = (id: string) =>
  apiRequest<void>(`/marketplaces/${id}`, {
    method: 'DELETE',
  });

// ============================================
// MARKETPLACE PRODUCT DATA
// ============================================

export const apiGetMarketplaceProductData = (productId: string, marketplaceId: string, countryCode: string) => {
  const params = new URLSearchParams({ productId, marketplaceId, countryCode });
  return apiRequest<ApiRecord | null>(`/marketplaces/product-data/find?${params}`);
};

export const apiGetProductDataByMarketplace = (marketplaceId: string, countryCode?: string) => {
  const params = new URLSearchParams({ marketplaceId, limit: '20000' });
  if (countryCode) params.append('countryCode', countryCode);
  return apiRequest<ApiRecord[]>(`/marketplaces/product-data?${params}`);
};

export const apiUpsertMarketplaceProductData = (data: ApiRecord) =>
  apiRequest<ApiRecord>('/marketplaces/product-data', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const apiBulkSaveMarketplaceProductData = (dataList: ApiRecord[]) =>
  apiRequest<{ added: number; updated: number }>('/marketplaces/product-data/bulk', {
    method: 'POST',
    body: JSON.stringify({ dataList }),
  });

// ============================================
// CATEGORY COLUMN DEFAULTS
// ============================================

export const apiGetCategoryColumnDefaults = (marketplaceId: string, countryCode: string, category: string, fulfillmentType?: string) => {
  const params = new URLSearchParams({ marketplaceId, countryCode, category });
  if (fulfillmentType) params.append('fulfillmentType', fulfillmentType);
  return apiRequest<ApiRecord | null>(`/marketplaces/category-defaults/find?${params}`);
};

export const apiGetCategoryDefaultsByMarketplace = (marketplaceId: string, countryCode?: string) => {
  const params = new URLSearchParams({ marketplaceId, limit: '20000' });
  if (countryCode) params.append('countryCode', countryCode);
  return apiRequest<ApiRecord[]>(`/marketplaces/category-defaults?${params}`);
};

export const apiUpsertCategoryColumnDefaults = (defaults: ApiRecord) =>
  apiRequest<ApiRecord>('/marketplaces/category-defaults', {
    method: 'POST',
    body: JSON.stringify(defaults),
  });

export const apiDeleteCategoryColumnDefaults = (id: string) =>
  apiRequest<void>(`/marketplaces/category-defaults/${id}`, {
    method: 'DELETE',
  });

export const apiDeleteCategoryDefaultsByCountry = (marketplaceId: string, countryCode: string) =>
  apiRequest<void>(`/marketplaces/category-defaults/by-country?marketplaceId=${marketplaceId}&countryCode=${countryCode}`, {
    method: 'DELETE',
  });

export const apiDeleteCategoryDefaultsByMarketplace = (marketplaceId: string) =>
  apiRequest<{ deleted: number }>(`/marketplaces/category-defaults/by-marketplace?marketplaceId=${marketplaceId}`, {
    method: 'DELETE',
  });

export const apiBulkUpsertCategoryColumnDefaults = (defaults: ApiRecord[]) =>
  apiRequest<{ imported: number; updated: number }>('/marketplaces/category-defaults/bulk', {
    method: 'POST',
    body: JSON.stringify({ defaults }),
  });

export const apiGetAllCategoryColumnDefaults = () =>
  apiRequest<ApiRecord[]>('/marketplaces/category-defaults/all?limit=20000');

// ============================================
// SETTINGS
// ============================================

export const apiGetAllSettings = () =>
  apiRequest<Record<string, unknown>>('/settings');

export const apiGetSetting = <T>(key: string) =>
  apiRequest<T>(`/settings/${key}`);

export const apiSaveSetting = (key: string, value: unknown) =>
  apiRequest<{ key: string; value: unknown }>(`/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });

export const apiDeleteSetting = (key: string) =>
  apiRequest<void>(`/settings/${key}`, {
    method: 'DELETE',
  });

// ============================================
// IMPORT HISTORY
// ============================================

export const apiGetImportHistory = (limit = 50) =>
  apiRequest<ApiRecord[]>(`/settings/import/history?limit=${limit}`);

export const apiSaveImportHistory = (history: ApiRecord) =>
  apiRequest<ApiRecord>('/settings/import/history', {
    method: 'POST',
    body: JSON.stringify(history),
  });

// ============================================
// DATABASE RESET
// ============================================

export const apiClearAllProducts = () =>
  apiRequest<void>('/settings/database/products', {
    method: 'DELETE',
    body: JSON.stringify({ confirm: 'DELETE_ALL_PRODUCTS' }),
  });

export const apiResetDatabase = () =>
  apiRequest<void>('/settings/database/all', {
    method: 'DELETE',
    body: JSON.stringify({ confirm: 'DELETE_ALL_DATA' }),
  });

// ============================================
// SHIPPING CARRIERS
// ============================================

export const apiGetAllShippingCarriers = () =>
  apiRequest<ApiRecord[]>('/shipping/carriers');

export const apiGetShippingCarrier = (id: string) =>
  apiRequest<ApiRecord>(`/shipping/carriers/${id}`);

export const apiSaveShippingCarrier = (carrier: ApiRecord) =>
  apiRequest<ApiRecord>('/shipping/carriers', {
    method: 'POST',
    body: JSON.stringify(carrier),
  });

export const apiUpdateShippingCarrier = (carrier: ApiRecord & { id: string }) =>
  apiRequest<ApiRecord>(`/shipping/carriers/${carrier.id}`, {
    method: 'PUT',
    body: JSON.stringify(carrier),
  });

export const apiDeleteShippingCarrier = (id: string) =>
  apiRequest<void>(`/shipping/carriers/${id}`, {
    method: 'DELETE',
  });

// ============================================
// SHIPPING ROUTES
// ============================================

export const apiGetAllShippingRoutes = () =>
  apiRequest<ApiRecord[]>('/shipping/routes');

export const apiGetShippingRoute = (id: string) =>
  apiRequest<ApiRecord>(`/shipping/routes/${id}`);

export const apiSaveShippingRoute = (route: ApiRecord) =>
  apiRequest<ApiRecord>('/shipping/routes', {
    method: 'POST',
    body: JSON.stringify(route),
  });

export const apiUpdateShippingRoute = (route: ApiRecord & { id: string }) =>
  apiRequest<ApiRecord>(`/shipping/routes/${route.id}`, {
    method: 'PUT',
    body: JSON.stringify(route),
  });

export const apiDeleteShippingRoute = (id: string) =>
  apiRequest<void>(`/shipping/routes/${id}`, {
    method: 'DELETE',
  });

// ============================================
// CARRIER ROUTE RATES
// ============================================

export const apiGetAllCarrierRouteRates = () =>
  apiRequest<ApiRecord[]>('/shipping/carrier-rates');

export const apiGetCarrierRouteRates = (carrierId: string, routeId: string) => {
  const params = new URLSearchParams({ carrierId, routeId });
  return apiRequest<ApiRecord | null>(`/shipping/carrier-rates/find?${params}`);
};

export const apiUpsertCarrierRouteRates = (rates: ApiRecord) =>
  apiRequest<ApiRecord>('/shipping/carrier-rates', {
    method: 'POST',
    body: JSON.stringify(rates),
  });

export const apiDeleteCarrierRouteRates = (id: string) =>
  apiRequest<void>(`/shipping/carrier-rates/${id}`, {
    method: 'DELETE',
  });

// ============================================
// ROUTE SHIPPING CONFIG
// ============================================

export const apiGetRouteShippingConfig = (routeId: string) =>
  apiRequest<ApiRecord | null>(`/shipping/route-config/${routeId}`);

export const apiGetAllRouteShippingConfigs = () =>
  apiRequest<ApiRecord[]>('/shipping/route-config');

export const apiUpsertRouteShippingConfig = (config: ApiRecord) =>
  apiRequest<ApiRecord>('/shipping/route-config', {
    method: 'POST',
    body: JSON.stringify(config),
  });

// ============================================
// DATABASE STATS
// ============================================

export const apiGetDatabaseStats = async () => {
  const [products, marketplaces, carriers] = await Promise.all([
    apiGetAllProducts().catch(() => []),
    apiGetAllMarketplaces().catch(() => []),
    apiGetAllShippingCarriers().catch(() => []),
  ]);

  return {
    productsCount: products.length,
    skuMappingsCount: 0, // Would need separate endpoint
    categoryExpensesCount: 0,
    importHistoryCount: 0,
    lastUpdated: new Date(),
    marketplacesCount: marketplaces.length,
    carriersCount: carriers.length,
  };
};

// ============================================
// DATABASE BACKUP/RESTORE (via API)
// ============================================

export const apiExportDatabase = () =>
  apiRequest<Record<string, unknown>>('/settings/export');

export const apiImportDatabase = (backup: Record<string, unknown>, clearExisting = true) =>
  apiRequest<{ message: string }>('/settings/import', {
    method: 'POST',
    body: JSON.stringify({ backup, clearExisting }),
  });

// ============================================
// EXCHANGE RATES
// ============================================

export interface ExchangeRate {
  id: string;
  currency_code: string;
  currency_name: string;
  rate_to_usd: number;
  source: string;
  fetched_at: string;
  updated_at: string;
}

export const apiGetAllExchangeRates = () =>
  apiRequest<ExchangeRate[]>('/settings/exchange-rates');

export const apiGetExchangeRate = (currency: string) =>
  apiRequest<ExchangeRate>(`/settings/exchange-rates/${currency}`);

export const apiSaveExchangeRate = (rate: { currency_code: string; currency_name?: string; rate_to_usd: number; source?: string }) =>
  apiRequest<ExchangeRate>('/settings/exchange-rates', {
    method: 'POST',
    body: JSON.stringify(rate),
  });

export const apiBulkSaveExchangeRates = (rates: Array<{ currency_code: string; currency_name?: string; rate_to_usd: number }>, source = 'api') =>
  apiRequest<ExchangeRate[]>('/settings/exchange-rates/bulk', {
    method: 'POST',
    body: JSON.stringify({ rates, source }),
  });

export const apiDeleteExchangeRate = (currency: string) =>
  apiRequest<void>(`/settings/exchange-rates/${currency}`, {
    method: 'DELETE',
  });

// ============================================
// SKU MANAGEMENT (New API)
// ============================================

export interface SKUData {
  id: string;
  product_id: string;
  marketplace_id: string;
  country_code: string;
  sku: string;
  asin?: string;
  listing_price?: number;
  fulfillment_type?: string;
  status?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  marketplace_name?: string;
  marketplace_short_name?: string;
  marketplace_icon?: string;
  product_name?: string;
  product_category?: string;
}

export interface ImportTemplate {
  id: string;
  name: string;
  marketplace_id: string;
  country_code?: string;
  columns: Array<{
    header: string;
    maps_to: string;
    required: boolean;
  }>;
  has_header_row: boolean;
  delimiter: string;
  is_system: boolean;
  is_default: boolean;
  marketplace_name?: string;
  marketplace_short_name?: string;
}

// Get all SKUs for a product
export const apiGetProductSKUs = (productId: string) =>
  apiRequest<SKUData[]>(`/sku/product/${productId}`);

// Get all SKUs for a marketplace
export const apiGetMarketplaceSKUs = (marketplaceId: string, countryCode?: string) => {
  const params = countryCode ? `?country_code=${countryCode}` : '';
  return apiRequest<SKUData[]>(`/sku/marketplace/${marketplaceId}${params}`);
};

// Create or update a SKU
export const apiSaveSKU = (sku: Partial<SKUData>) =>
  apiRequest<SKUData>('/sku', {
    method: 'POST',
    body: JSON.stringify(sku),
  });

// Update a SKU
export const apiUpdateSKU = (id: string, updates: Partial<SKUData>) =>
  apiRequest<SKUData>(`/sku/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });

// Delete a SKU
export const apiDeleteSKU = (id: string) =>
  apiRequest<void>(`/sku/${id}`, {
    method: 'DELETE',
  });

// Bulk create/update SKUs
export const apiBulkSaveSKUs = (mappings: Partial<SKUData>[]) =>
  apiRequest<{ created: number; updated: number; errors: string[] }>('/sku/bulk', {
    method: 'POST',
    body: JSON.stringify({ mappings }),
  });

// Get SKU statistics
export const apiGetSKUStats = () =>
  apiRequest<{
    total_skus: number;
    products_with_skus: number;
    marketplaces_used: number;
    by_marketplace: Record<string, number>;
  }>('/sku/stats');

// Get all import templates
export const apiGetImportTemplates = (marketplaceId?: string) => {
  const params = marketplaceId ? `?marketplace_id=${marketplaceId}` : '';
  return apiRequest<ImportTemplate[]>(`/sku/templates${params}`);
};

// Create an import template
export const apiSaveImportTemplate = (template: Partial<ImportTemplate>) =>
  apiRequest<ImportTemplate>('/sku/templates', {
    method: 'POST',
    body: JSON.stringify(template),
  });

// Delete an import template
export const apiDeleteImportTemplate = (id: string) =>
  apiRequest<void>(`/sku/templates/${id}`, {
    method: 'DELETE',
  });

// Search products for matching
export const apiSearchProductsForMatch = (query: string) =>
  apiRequest<Array<{ id: string; name: string; category: string; base_cost: number; size: number }>>(`/sku/match?q=${encodeURIComponent(query)}`);

// Get unmatched SKUs
export const apiGetUnmatchedSKUs = () =>
  apiRequest<SKUData[]>('/sku/unmatched');

// ============================================
// MARKETPLACE PROFIT CONFIGS
// ============================================

export interface FBAConfig {
  shippingPerDesi: number;      // Gemi bedeli ($/desi)
  warehousePercent: number;     // Depo-İdare %
}

export interface FBMFromTRConfig {
  customsDutyPercent: number;   // Varsayılan gümrük vergisi %
  categoryDuties?: Array<{ category: string; dutyPercent: number }>;
  ddpFee: number;               // DDP ücreti (sabit)
}

export interface FBMFromLocalConfig {
  warehousePercent: number;     // Depo-İdare %
}

export interface FBMConfig {
  shippingMode: 'TR' | 'LOCAL' | 'BOTH';
  fromTR?: FBMFromTRConfig;
  fromLocal?: FBMFromLocalConfig;
}

export interface GSTConfig {
  gstPercent: number;
  importThreshold?: number;
  includedInPrice?: boolean;  // VAT fiyata dahil mi?
}

export interface MarketplaceProfitConfig {
  id: string;
  marketplace_code: string;
  marketplace_name: string;
  currency: string;
  fba_config: FBAConfig;
  fbm_config: FBMConfig;
  gst_config?: GSTConfig | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const apiGetAllProfitConfigs = () =>
  apiRequest<MarketplaceProfitConfig[]>('/marketplaces/profit-configs');

export const apiGetProfitConfig = (marketplaceCode: string) =>
  apiRequest<MarketplaceProfitConfig>(`/marketplaces/profit-configs/${marketplaceCode}`);

export const apiSaveProfitConfig = (config: Partial<MarketplaceProfitConfig>) =>
  apiRequest<MarketplaceProfitConfig>('/marketplaces/profit-configs', {
    method: 'POST',
    body: JSON.stringify(config),
  });

export const apiDeleteProfitConfig = (marketplaceCode: string) =>
  apiRequest<void>(`/marketplaces/profit-configs/${marketplaceCode}`, {
    method: 'DELETE',
  });

// ============================================
// AMAZON EXPENSES (from AMZSellMetrics)
// ============================================

export interface AmazonExpenseEntry {
  sellingFees: number;
  fbaFees: number;
  otherTransactionFees: number;
  vat: number;
  advertisingCost: number;
  refundLoss: number;
  liquidations: number;
  totalOrders: number;
  totalRevenue: number;
  totalQuantity: number;
  sellingFeePercent: number;
  fbaFeePercent: number;
  advertisingPercent: number;
  refundLossPercent: number;
}

export interface AmazonExpensesImport {
  version: number;
  exportedAt: string;
  sourceApp: string;
  period: {
    start: string;
    end: string;
  };
  marketplaces: {
    [marketplaceCode: string]: {
      FBA?: AmazonExpenseEntry;
      FBM?: AmazonExpenseEntry;
      'FBM-US'?: AmazonExpenseEntry;
    };
  };
  summary: {
    totalMarketplaces: number;
    totalRevenue: number;
    totalOrders: number;
  };
}

export interface AmazonExpenseRecord {
  id: string;
  marketplace_code: string;
  fulfillment_type: 'FBA' | 'FBM' | 'FBM-US';
  period_start: string;
  period_end: string;
  selling_fees: number;
  fba_fees: number;
  other_transaction_fees: number;
  vat: number;
  advertising_cost: number;
  refund_loss: number;
  liquidations: number;
  total_orders: number;
  total_revenue: number;
  total_quantity: number;
  selling_fee_percent: number;
  fba_fee_percent: number;
  advertising_percent: number;
  refund_loss_percent: number;
  imported_at: string;
  source_file?: string;
}

export const apiGetAllAmazonExpenses = () =>
  apiRequest<AmazonExpenseRecord[]>('/marketplaces/amazon-expenses/all');

export const apiGetAmazonExpensesByMarketplace = (marketplaceCode: string) =>
  apiRequest<AmazonExpenseRecord[]>(`/marketplaces/amazon-expenses/${marketplaceCode}`);

export const apiGetLatestAmazonExpenses = () =>
  apiRequest<AmazonExpenseRecord[]>('/marketplaces/amazon-expenses-latest/all');

export const apiImportAmazonExpenses = (data: AmazonExpensesImport, sourceFile?: string) =>
  apiRequest<{ imported: number; updated: number }>('/marketplaces/amazon-expenses/import', {
    method: 'POST',
    body: JSON.stringify({ ...data, sourceFile }),
  });

export const apiDeleteAmazonExpenses = (marketplaceCode: string, fulfillmentType: string, periodStart?: string, periodEnd?: string) => {
  let url = `/marketplaces/amazon-expenses/${marketplaceCode}/${fulfillmentType}`;
  if (periodStart && periodEnd) {
    url += `?periodStart=${periodStart}&periodEnd=${periodEnd}`;
  }
  return apiRequest<{ message: string }>(url, { method: 'DELETE' });
};

// ============================================
// CHANNEL PRICES (cross-channel comparison)
// ============================================

export interface ChannelInfo {
  code: string;
  platform: string;
  store_label: string;
  parser_type: string;
  is_active: boolean;
  sort_order: number;
  row_count: string;
  unmatched_count: string;
  last_captured_at: string | null;
}

export interface ComparisonCell {
  price: number | null;
  compare_at: number | null;
  currency: string;
  status: string | null;
}

export interface ComparisonRow {
  iwasku: string;
  name: string;
  category: string;
  prices: Record<string, ComparisonCell>;
  min_price: number | null;
  max_price: number | null;
  channel_count: string;
}

export interface ChannelBulkRow {
  marketplace_sku: string;
  price?: number | null;
  compare_at?: number | null;
  status?: string | null;
  title?: string | null;
  extra?: Record<string, unknown> | null;
}

export interface ChannelBulkResult {
  inserted: number;
  updated: number;
  matched: number;
  unmatched: number;
  duplicatesRemoved?: number;
  byLayer: Record<string, number>;
}

export interface UnmatchedRow {
  id: string;
  channel_code: string;
  country_code: string;
  marketplace_sku: string;
  price: number | null;
  status: string | null;
  captured_at: string;
}

export const apiGetChannels = () =>
  apiRequest<ChannelInfo[]>('/channel-prices/channels');

export const apiGetChannelComparison = (country = 'US', category?: string, search?: string) => {
  const params = new URLSearchParams({ country });
  if (category) params.append('category', category);
  if (search) params.append('search', search);
  return apiRequest<ComparisonRow[]>(`/channel-prices/comparison?${params}`);
};

export const apiBulkSaveChannelPrices = (channel_code: string, country_code: string, rows: ChannelBulkRow[], source?: string) =>
  apiRequest<ChannelBulkResult>('/channel-prices/bulk', {
    method: 'POST',
    body: JSON.stringify({ channel_code, country_code, rows, source }),
  });

export const apiGetChannelUnmatched = (channel?: string, country = 'US') => {
  const params = new URLSearchParams({ country });
  if (channel) params.append('channel', channel);
  return apiRequest<UnmatchedRow[]>(`/channel-prices/unmatched?${params}`);
};

export const apiSaveChannelMapping = (marketplace_sku: string, iwasku: string, channel_code?: string | null) =>
  apiRequest<{ mapped: boolean; rows_resolved: number }>('/channel-prices/mappings', {
    method: 'POST',
    body: JSON.stringify({ marketplace_sku, iwasku, channel_code }),
  });

// ============================================
// PROFITABILITY (SCENARIO)
// ============================================

export interface ProfitabilityParent {
  parent: string;
  productCount: number;
  cost: number | null;
  size: number | null;
  category: string | null;
  mixed: boolean;
}

export const apiGetProfitabilityParents = () =>
  apiRequest<ProfitabilityParent[]>('/profitability/parents');

// ============================================
// HEALTH CHECK
// ============================================

export const apiHealthCheck = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE}/health`);
    const data = await response.json();
    return data.success === true;
  } catch {
    return false;
  }
};

// ============================================
// SKU MASTER SYNC
// ============================================

// FIX 12: Centralized SKU Master sync (removes hardcoded IP)
export const apiSyncSKUMaster = () =>
  apiRequest<{ updated: number; inserted: number; total: number; withCost: number; withSize: number }>(
    '/products/mapping/amazon-analyzer/sync',
    { method: 'POST' }
  );

// ============================================
// ADVANCED PRICING (FBA/FBM)
// ============================================

// Get shipping rate brackets for a specific route
export const apiGetShippingRates = (routeCode: string) =>
  apiRequest<ApiRecord>(`/marketplaces/shipping-rates/${routeCode}`);

// Get all shipping routes
export const apiGetAllShippingRates = (isActive?: boolean) => {
  const params = new URLSearchParams();
  if (isActive !== undefined) params.append('isActive', String(isActive));
  const queryString = params.toString();
  return apiRequest<ApiRecord[]>(`/marketplaces/shipping-rates${queryString ? `?${queryString}` : ''}`);
};

// Update/create shipping rate brackets
export const apiUpdateShippingRates = (data: {
  routeCode: string;
  routeName?: string;
  currency: string;
  brackets: Array<{ desi: number; rate: number }>;
  isActive?: boolean;
  notes?: string;
}) =>
  apiRequest<ApiRecord>('/marketplaces/shipping-rates', {
    method: 'POST',
    body: JSON.stringify(data),
  });

